/**
 * chatPush.ts — 自社チャット（LP チャット）のデスクトップ通知（Web Push）を LINE ページから購読する（2026-10-26）
 *
 * 設計: booking-platform/md/customer-chat.md §13 ／ md/master_md/page-line-chat.md「/admin の統合」
 * LP 管理画面（/admin）の「デスクトップ通知」を移したもの。仕組みは LP と同じ:
 *   1. プッシュ専用 SW（public/chat-push-sw.js・スコープ /chat-push/）を登録
 *   2. 通知を許可してもらう
 *   3. LP の VAPID 公開鍵（get_vapid_public_key・公開）で PushManager.subscribe
 *   4. LP の register_push_subscription（Bearer）に保存 → LP が顧客の新着で全購読へ送る
 * 失敗は理由を返し、logEvent に残す（無言の失敗を作らない）。
 */
import { getReservationChatApiUrl } from '../config/backend';
import { getAuthToken } from '../api/dispatchClient';
import { chatAuthHeaders } from '../lib/reservationChat';
import { logEvent } from '../lib/eventLog';

export const CHAT_PUSH_SW_URL = '/chat-push-sw.js';
export const CHAT_PUSH_SCOPE = '/chat-push/';

export type ChatPushState = 'unsupported' | 'denied' | 'off' | 'on';

const token = (): string | null => {
  const t = getAuthToken();
  if (t) return t;
  try { return window.localStorage.getItem('authToken'); } catch { return null; }
};

export const isChatPushSupported = (): boolean =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** base64url（VAPID 公開鍵）→ Uint8Array */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

const getRegistration = async (): Promise<ServiceWorkerRegistration | undefined> =>
  navigator.serviceWorker.getRegistration(CHAT_PUSH_SCOPE);

/** 今の状態（この端末・このブラウザ） */
export async function getChatPushState(): Promise<ChatPushState> {
  if (!isChatPushSupported() || !getReservationChatApiUrl()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return sub ? 'on' : 'off';
  } catch (e) {
    logEvent({ action: 'chat_push.state', result: 'fail', err: e });
    return 'off';
  }
}

/** 購読する。成功で null、失敗は人が読める理由 */
export async function subscribeChatPush(): Promise<string | null> {
  const api = getReservationChatApiUrl();
  if (!isChatPushSupported() || !api) return 'このブラウザ（またはこの店舗）ではデスクトップ通知を使えません';
  const started = Date.now();
  try {
    const reg = await navigator.serviceWorker.register(CHAT_PUSH_SW_URL, { scope: CHAT_PUSH_SCOPE });
    let perm = Notification.permission;
    if (perm === 'default') perm = await Notification.requestPermission();
    if (perm !== 'granted') return '通知が許可されていません（ブラウザの設定で通知を許可してください）';

    const keyRes = await fetch(`${api}?action=get_vapid_public_key&_t=${Date.now()}`);
    const keyData = await keyRes.json().catch(() => null);
    if (!keyRes.ok || !keyData?.public_key) throw new Error(`vapid key: HTTP ${keyRes.status} ${JSON.stringify(keyData).slice(0, 120)}`);

    // SW が有効になるまで待つ（スコープ外のページなので navigator.serviceWorker.ready は使えない）
    const sw = reg.installing || reg.waiting;
    if (sw && sw.state !== 'activated') {
      await new Promise<void>((resolve) => {
        const done = () => { if (sw.state === 'activated') resolve(); };
        sw.addEventListener('statechange', done);
        setTimeout(resolve, 5000);
      });
    }

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyData.public_key) as BufferSource });
    }
    const json: any = sub.toJSON ? sub.toJSON() : sub;
    const res = await fetch(`${api}?action=register_push_subscription`, {
      method: 'POST',
      headers: chatAuthHeaders(token()),
      body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys || {} }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) throw new Error(`register: HTTP ${res.status} ${JSON.stringify(data).slice(0, 120)}`);
    logEvent({ action: 'chat_push.subscribe', result: 'ok', ms: Date.now() - started });
    return null;
  } catch (e) {
    logEvent({ action: 'chat_push.subscribe', result: 'fail', ms: Date.now() - started, err: e });
    return `デスクトップ通知を設定できませんでした: ${e instanceof Error ? e.message : String(e)}`;
  }
}

/** 購読をやめる（LP からも消す）。成功で null */
export async function unsubscribeChatPush(): Promise<string | null> {
  const api = getReservationChatApiUrl();
  const started = Date.now();
  try {
    const reg = await getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    if (api) {
      const res = await fetch(`${api}?action=unregister_push_subscription`, {
        method: 'POST',
        headers: chatAuthHeaders(token()),
        body: JSON.stringify({ endpoint }),
      });
      if (!res.ok) throw new Error(`unregister: HTTP ${res.status}`);
    }
    logEvent({ action: 'chat_push.unsubscribe', result: 'ok', ms: Date.now() - started });
    return null;
  } catch (e) {
    logEvent({ action: 'chat_push.unsubscribe', result: 'fail', ms: Date.now() - started, err: e });
    return `デスクトップ通知を解除できませんでした: ${e instanceof Error ? e.message : String(e)}`;
  }
}
